import React, { useEffect, useState } from "react";
import { api } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { PageHeader, Card, Button, Input, Select } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, X, ShieldCheck, Warning, ShieldWarning, TrashSimple } from "@phosphor-icons/react";

const PropFirm = () => {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/propfirm").then(r => r.data);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm(lang === "cs" ? "Smazat účet?" : "Delete account?")) return;
    await api.delete(`/propfirm/${id}`);
    toast.success(lang === "cs" ? "Smazáno" : "Deleted");
    load();
  };

  return (
    <div>
      <PageHeader title={t("propfirm.title")} sub={t("propfirm.sub")} action={
        <Button onClick={() => setOpen(true)} data-testid="pf-add-btn" className="flex items-center gap-2"><Plus weight="bold" size={16} /> {t("propfirm.add")}</Button>
      } />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading...</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center text-zinc-500" data-testid="pf-empty">
            <ShieldCheck weight="thin" size={64} className="mx-auto mb-4 text-zinc-700" />
            <p className="text-sm">{t("propfirm.empty")}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {items.map((acc) => <PFCard key={acc.id} acc={acc} t={t} lang={lang} onDelete={() => del(acc.id)} />)}
          </div>
        )}
      </div>

      {open && <PFDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} t={t} lang={lang} />}
    </div>
  );
};

const PFCard = ({ acc, t, lang, onDelete }) => {
  const c = acc.computed;
  const StatusIcon = c.status === "safe" ? ShieldCheck : c.status === "warning" ? Warning : ShieldWarning;
  const statusColor = c.status === "safe" ? "text-green-400" : c.status === "warning" ? "text-yellow-400" : "text-red-400";
  const statusBg = c.status === "safe" ? "bg-green-500/10 border-green-500/30" : c.status === "warning" ? "bg-yellow-500/10 border-yellow-500/30" : "bg-red-500/10 border-red-500/30";
  return (
    <Card className="p-6" data-testid={`pf-card-${acc.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{acc.firm}</div>
          <h3 className="text-lg font-medium text-zinc-100 mt-1">{acc.name}</h3>
          <div className="text-sm font-mono text-zinc-500 mt-0.5">
            ${acc.account_size.toLocaleString()} · <span className="text-zinc-300">${(acc.current_balance || 0).toLocaleString()}</span>
          </div>
          {acc.computed_from_journal && (
            <div className="text-[10px] font-mono uppercase tracking-wider text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5 inline-block mt-1.5" data-testid={`pf-journal-badge-${acc.id}`}>
              {lang === "cs" ? `Živě z deníku · ${acc.journal_trades} obchodů` : `Live from journal · ${acc.journal_trades} trades`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono uppercase tracking-wider border ${statusBg} ${statusColor}`}>
            <StatusIcon weight="bold" size={12} /> {t(`propfirm.status.${c.status}`)}
          </span>
          <button onClick={onDelete} className="text-zinc-500 hover:text-red-400 p-1 transition-colors"><TrashSimple size={14} /></button>
        </div>
      </div>

      <div className="space-y-3">
        <Bar label={lang === "cs" ? "Denní ztráta" : "Daily loss"} value={c.daily_usage} suffix="%" warning={c.daily_usage > 80 ? "danger" : c.daily_usage > 50 ? "warning" : "safe"} />
        <Bar label={lang === "cs" ? "Drawdown" : "Drawdown"} value={c.dd_usage} suffix="%" warning={c.dd_usage > 80 ? "danger" : c.dd_usage > 50 ? "warning" : "safe"} />
        <Bar label={lang === "cs" ? "Profit target" : "Profit target"} value={c.target_progress} suffix="%" warning="good" />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-zinc-800">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("propfirm.recRisk")}</div>
          <div className="text-xl font-mono font-medium text-blue-400">{c.recommended_risk_pct}%</div>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("propfirm.toTarget")}</div>
          <div className="text-xl font-mono font-medium text-zinc-100">{c.remaining_to_target_pct}%</div>
        </div>
      </div>
    </Card>
  );
};

const Bar = ({ label, value, suffix, warning }) => {
  const v = Math.min(100, Math.max(0, value || 0));
  const color = warning === "danger" ? "bg-red-500" : warning === "warning" ? "bg-yellow-500" : warning === "good" ? "bg-blue-500" : "bg-green-500";
  return (
    <div>
      <div className="flex justify-between text-xs font-mono mb-1">
        <span className="uppercase tracking-wider text-zinc-500">{label}</span>
        <span className="text-zinc-200">{value}{suffix}</span>
      </div>
      <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-300`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

const PFDialog = ({ onClose, onSaved, t, lang }) => {
  const [form, setForm] = useState({
    firm: "Moje prop firma",
    name: "Challenge #1",
    account_size: 100000,
    daily_loss_limit_pct: 5,
    max_drawdown_pct: 10,
    profit_target_pct: 10,
    current_balance: 100000,
    current_daily_loss: 0,
    days_traded: 0,
    min_trading_days: 4,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/propfirm", {
        ...form,
        account_size: parseFloat(form.account_size),
        daily_loss_limit_pct: parseFloat(form.daily_loss_limit_pct),
        max_drawdown_pct: parseFloat(form.max_drawdown_pct),
        profit_target_pct: parseFloat(form.profit_target_pct),
        current_balance: parseFloat(form.current_balance),
        current_daily_loss: parseFloat(form.current_daily_loss),
        days_traded: parseInt(form.days_traded || 0),
      });
      toast.success(lang === "cs" ? "Účet přidán" : "Account added");
      onSaved();
    } catch (err) {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pf-dialog">
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <h3 className="text-lg font-medium text-zinc-100">{t("propfirm.add")}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X weight="bold" size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input data-testid="pf-firm" label={t("propfirm.firm")} value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} placeholder="Název prop firmy" required />
          <Input data-testid="pf-name" label={t("propfirm.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input data-testid="pf-size" type="number" label={t("propfirm.size")} value={form.account_size} onChange={(e) => setForm({ ...form, account_size: e.target.value })} required />
          <Input data-testid="pf-balance" type="number" label={t("propfirm.balance")} value={form.current_balance} onChange={(e) => setForm({ ...form, current_balance: e.target.value })} required />
          <Input data-testid="pf-daily" type="number" step="0.1" label={t("propfirm.daily")} value={form.daily_loss_limit_pct} onChange={(e) => setForm({ ...form, daily_loss_limit_pct: e.target.value })} />
          <Input data-testid="pf-dd" type="number" step="0.1" label={t("propfirm.dd")} value={form.max_drawdown_pct} onChange={(e) => setForm({ ...form, max_drawdown_pct: e.target.value })} />
          <Input data-testid="pf-target" type="number" step="0.1" label={t("propfirm.target")} value={form.profit_target_pct} onChange={(e) => setForm({ ...form, profit_target_pct: e.target.value })} />
          <Input data-testid="pf-dailyloss" type="number" step="any" label={t("propfirm.dailyloss")} value={form.current_daily_loss} onChange={(e) => setForm({ ...form, current_daily_loss: e.target.value })} />
          <Input data-testid="pf-days" type="number" label={t("propfirm.days")} value={form.days_traded} onChange={(e) => setForm({ ...form, days_traded: e.target.value })} />
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} data-testid="pf-save">{saving ? "..." : t("common.save")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropFirm;
